import { createAsyncThunk } from '@reduxjs/toolkit';
import { getPickingRouteRequest, routePickRequest, routeShortRequest, routeUndoRequest } from './operatorPickingApi';
import {
  ROUTE_PHASE,
  buildShortBody,
  buildTakeBody,
  buildUndoBody,
  findStop,
  readRouteCode,
  routeIdOf,
  shortArgOf,
  stopHintOf,
  stopUnitOf,
  takeArgOf,
} from './operatorPickingRouteUtils';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { OPERATOR_ERROR, localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, sameAction, sendWrite } from '../operator/operatorIdempotency';
import { SCAN_GATE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { offScreenMeta } from '../operator/operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());

const routeVisit = (state) => state.operatorPickingRoute;

const writeOptions = {
  ...idempotentThunkOptions,
  // Keyed by route, so a write left out on one does not keep the next one's buttons busy.
  getPendingMeta: ({ arg }) => ({ pendingKey: arg?.routeId }),
};

/** @param {{orderIds: number[]}} arg */
export const fetchPickingRoute = createAsyncThunk(
  'operatorPickingRoute/fetchRoute',
  async ({ orderIds }, { getState, rejectWithValue }) => {
    try {
      const { data } = await getPickingRouteRequest(warehouseOf(getState), orderIds);
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos armar la ruta.'));
    }
  },
  { getPendingMeta: ({ arg }) => ({ pendingKey: routeIdOf(arg.orderIds) }) }
);

/** Whether the route on screen takes a read now: loaded, on a stop, and with no write out or unconfirmed. */
export const isRouteBusy = (visit) =>
  !visit.route ||
  Boolean(visit.pending || visit.lastWrite) ||
  ![ROUTE_PHASE.SLOT, ROUTE_PHASE.ITEM].includes(visit.stop.phase);

/**
 * A code read on the route, worked out on the phone (`readRouteCode`): the
 * stop's slot verifies it, then each product read counts its units. Nothing
 * is sent: «Tomar» sends what was counted. Counting, the same product held up
 * to the camera again (`again`) counts again. Before the stop is verified, its
 * slot label beside a bigger label in view verifies it too; a product is never
 * taken for another in view.
 *
 * @param {{code: string, source: string, again?: boolean, alternatives?: string[]}} read
 */
export const scanRouteCode = createAsyncThunk(
  'operatorPickingRoute/scanCode',
  async ({ code, alternatives = [] }, { getState, fulfillWithValue, rejectWithValue }) => {
    const visit = routeVisit(getState());
    const stop = findStop(visit.route, visit.stop.slotCode);
    const readAt = (candidate) =>
      readRouteCode(
        { route: visit.route, stop, verified: visit.stop.verified, localCounts: visit.localCounts },
        cleanCode(candidate)
      );
    const outcome = readAt(code);
    if (outcome.error) {
      const slotBeside = !visit.stop.verified && alternatives.find((candidate) => readAt(candidate).kind === 'slot');
      if (slotBeside) return fulfillWithValue({ ...readAt(slotBeside), routeId: visit.routeId }, { matched: slotBeside });
      return rejectWithValue(localError(outcome.error));
    }
    if (outcome.kind === 'slot') return { ...outcome, routeId: visit.routeId };
    const item = stop.items.find((candidate) => candidate.key === outcome.key);
    return {
      ...outcome,
      routeId: visit.routeId,
      counted: (visit.localCounts[outcome.key] || 0) + outcome.units,
      pending: Math.max(Number(item.quantity) - Number(item.picked), 0),
    };
  },
  {
    condition: (read, { getState }) => {
      const visit = routeVisit(getState());
      const gate = {
        busy: isRouteBusy(visit),
        lastCode: visit.scan.lastCode,
        countsRereads: visit.stop.phase === ROUTE_PHASE.ITEM,
        unitOf: (code) => stopUnitOf(findStop(visit.route, visit.stop.slotCode), code),
      };
      return scanGate(gate, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * Where a write's answer lands, read when it arrives. A route off screen gets
 * the band named after it (`meta.offScreenTask`). The route on screen takes
 * the answer only if it is the write its visit waits for; any other — sent on
 * an earlier visit — reloads it instead (`meta.earlierVisit`). A reload already
 * out read the route before this answer: it is asked again, and the one out
 * is ignored. The awaited answer says where the walk goes (`meta.stopHint`).
 */
const answerMeta = ({ getState, dispatch, requestId }, arg, route) => {
  const state = getState();
  const meta = offScreenMeta(state, OPERATOR_FLOW.PICKING, arg.routeId, arg.routeName);
  if (meta) return meta;
  const visit = routeVisit(state);
  const awaited = visit.lastWrite?.key === requestId;
  if (!awaited || visit.loading) dispatch(fetchPickingRoute({ orderIds: visit.orderIds }));
  if (!awaited) return { earlierVisit: true };
  const hint = route ? stopHintOf(route, visit.skippedStops, arg.slotCode) : null;
  return hint ? { stopHint: hint } : undefined;
};

/**
 * Takes units of an item at a stop, in one write: the server splits them
 * among the orders (`order_ids`) and answers the route recalculated, and the
 * `pick_key` that undoes it.
 *
 * @param {{routeId: string, routeName: string, slotCode: string, itemKey: string, sku: string,
 *   productCode: string, quantity: number, lot?: string, orderIds: number[], idempotencyKey?: string}} arg
 */
export const takeAtStop = createAsyncThunk(
  'operatorPickingRoute/take',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await routePickRequest(warehouseOf(getState), buildTakeBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg, data.route));
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos registrar la toma.'), answerMeta(thunkApi, arg));
    }
  },
  writeOptions
);

/**
 * Finding less than the route says at a stop: what was found is picked — 0
 * is said, not assumed — and the rest is left as a shortage.
 *
 * @param {{routeId: string, routeName: string, slotCode: string, itemKey: string, sku: string, productId: number,
 *   lot?: string, found: number, pending: number, reason: string, notes?: string, orderIds: number[],
 *   idempotencyKey?: string}} arg
 */
export const reportShortAtStop = createAsyncThunk(
  'operatorPickingRoute/short',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await routeShortRequest(warehouseOf(getState), buildShortBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg, data.route));
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos registrar el faltante.'), answerMeta(thunkApi, arg));
    }
  },
  writeOptions
);

/**
 * Why an undo was refused. A 409 that gives a `reason` is a rule of the floor
 * — the unit is already in an item, the order left picking, someone else
 * picked it —, not a clash to send again.
 */
const readUndoError = (error) => {
  const failure = readOperatorError(error, 'No pudimos deshacer la toma.');
  const reason = error?.response?.data?.reason;
  return failure.kind === OPERATOR_ERROR.CONFLICT && reason ? { ...failure, kind: OPERATOR_ERROR.REFUSED, reason } : failure;
};

/**
 * Gives the last take back: «Deshacer». Only who took it can, and only while
 * it is not in an item and its orders are still being picked.
 *
 * @param {{routeId: string, routeName: string, pickKey: string, slotCode: string, sku: string, units: number,
 *   idempotencyKey?: string}} arg
 */
export const undoLastPick = createAsyncThunk(
  'operatorPickingRoute/undo',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await routeUndoRequest(warehouseOf(getState), buildUndoBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg));
    } catch (error) {
      return rejectWithValue(readUndoError(error), answerMeta(thunkApi, arg));
    }
  },
  writeOptions
);

/** «Tomar» on the stop on screen: the same take after a lost answer goes under its first key. */
export const submitTake = () => (dispatch, getState) => {
  const arg = takeArgOf(routeVisit(getState()));
  return arg ? dispatch(sendWrite(takeAtStop, arg, routeVisit)) : null;
};

/**
 * The shortage sheet's primary.
 *
 * @param {string} itemKey
 * @param {{found: string|number, reason: string, notes?: string}} form
 */
export const submitRouteShort = (itemKey, form) => (dispatch, getState) => {
  const arg = shortArgOf(routeVisit(getState()), itemKey, form);
  return arg ? dispatch(sendWrite(reportShortAtStop, arg, routeVisit)) : null;
};

/** «Deshacer»: the last take of the route on screen. */
export const submitUndoLastPick = () => (dispatch, getState) => {
  const { lastPick, orderIds } = routeVisit(getState());
  return lastPick ? dispatch(sendWrite(undoLastPick, { ...lastPick, orderIds }, routeVisit)) : null;
};

/** The route's writes, by type: what its band and its own «Reintentar» send again. */
export const ROUTE_WRITES = {
  [takeAtStop.typePrefix]: takeAtStop,
  [reportShortAtStop.typePrefix]: reportShortAtStop,
  [undoLastPick.typePrefix]: undoLastPick,
};

/** The route's own «Reintentar»: the write whose answer was lost, under the same key. */
export const retryLastRouteWrite = () => (dispatch, getState) => {
  const { lastWrite, pending } = routeVisit(getState());
  const thunk = lastWrite && !pending ? ROUTE_WRITES[lastWrite.type] : null;
  return thunk ? dispatch(thunk(sameAction(lastWrite))) : null;
};
